<script lang="ts">
  import { asset } from '$app/paths';
  import { GraphicBlock } from '@reuters-graphics/graphics-components';
  import { partyToColour } from '$lib/constants/index';
  import { parties } from '$lib/constants/crosswalk';
  import { blocColors } from '$lib/constants/colors';

  interface Candidate {
    party?: string;
    name?: string;
    description?: string;
  }

  interface Props {
    title?: string;
    subHead?: string;
    candidates?: Candidate[];
    notes?: string;
    source?: string;
    altText?: string;
  }

  let {
    title: blockTitle = '',
    subHead = '',
    candidates = [],
    notes = '',
    source = '',
    altText = '',
  }: Props = $props();

  const notesText = $derived([notes, source].filter(Boolean).join('\n\n'));

  // Slugs of the photos that exist in src/statics/images — candidates without
  // one render a blank slot, and pick up their photo once the file is added.
  const photoSlugs = new Set(
    Object.keys(import.meta.glob('/src/statics/images/*.png')).map((path) =>
      (path.split('/').pop() || '').replace(/\.png$/, '')
    )
  );

  const slug = (value: string) =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  // "There is a Future" is the literal English translation of "Yesh Atid"
  // (Hebrew for "there is a future") — content sometimes uses the translated
  // name rather than the transliteration used elsewhere on the page. The
  // other entries here are candidate-block labels from rngs.io that name a
  // party (or combine a couple) differently than `crosswalk.ts`'s `parties`
  // list — without an alias they don't match anything there and the pill
  // falls back to the default gray.
  const partyLabelAliases: Record<string, string> = {
    'there is a future': 'Yesh Atid',
    'shas, utj': 'Shas',
    'israel our home': 'Israel Our Home',
    'the democrats': 'Democrats',
    'the reservists': 'Reservists',
    'the people of israel': 'People of Israel',
    'raam and the joint list': 'Joint List',
    'the joint list and raam': 'Joint List',
  };

  // rngs.io sometimes appends a parenthetical transliteration to a party's
  // English name (e.g. "Israel Our Home (Yisrael Beiteinu)", "Jewish Power
  // (Otzma Yehudit)") — strip it before matching so those additions don't
  // silently break the colour lookup and fall back to gray.
  const stripParenthetical = (label: string): string =>
    label.replace(/\s*\([^)]*\)\s*$/, '').trim();

  const partyKeyFromLabel = (label: string): string | undefined => {
    const base = stripParenthetical(label);
    const normalized = base.toLowerCase();
    const resolvedLabel = partyLabelAliases[normalized] ?? base;
    return parties.find(
      (party) =>
        party.label.toLowerCase() === resolvedLabel.trim().toLowerCase()
    )?.key;
  };

  // Matches this candidate regardless of word order or future rewording in
  // rngs.io (e.g. "Raam and The Joint List", "The Joint List and Raam") —
  // it should always get the base Arab-bloc yellow below, not `joint_list`'s
  // crosswalk colour, which is deliberately darkened a shade to distinguish
  // it from Ra'am on the poll chart.
  const isRaamJointList = (label: string): boolean => {
    const normalized = label.trim().toLowerCase();
    return normalized.includes('joint list') && normalized.includes('raam');
  };

  // Both sit in the neutral `other` bloc on the poll chart (gray lines). Here
  // Reservists keeps a gray pill (`other`'s own colour is near-white, too pale
  // for white text) and People of Israel gets far-right.
  const pillColourOverrides: Record<string, string> = {
    reservists: '#8b8b8b',
    people_of_israel: blocColors.farRight,
  };

  const partyPillColour = (label: string): string => {
    if (isRaamJointList(label || '')) return blocColors.arab;
    const key = partyKeyFromLabel(label || '');
    if (key && pillColourOverrides[key]) return pillColourOverrides[key];
    return key ? partyToColour(key) : '#cccccc';
  };
</script>

<GraphicBlock description={subHead} notes={notesText} ariaDescription={altText}>
  {#snippet title()}
    {#if blockTitle}
      <h2>{blockTitle}</h2>
    {/if}
  {/snippet}
  <div class="pm-candidates">
    {#each candidates as candidate}
      {@const partyColour = partyPillColour(candidate.party || '')}
      {@const hasPhoto = photoSlugs.has(slug(candidate.name || ''))}
      <div class="pm-candidate">
        <div class="pm-candidate__left">
          <div class="pm-candidate__headshot">
            {#if hasPhoto}
              <div
                class="pm-candidate__mount"
                style="background-color:{partyColour}"
              ></div>
              <div
                class="pm-candidate__photo"
                style="background-image:url({asset(
                  `/images/${slug(candidate.name || '')}.png`
                )})"
              ></div>
            {/if}
          </div>
          {#if hasPhoto}
            <div class="pm-candidate__name">{candidate.name}</div>
          {/if}
        </div>
        <div class="pm-candidate__right">
          <div class="pm-candidate__party">
            <span style="background-color:{partyColour}">{candidate.party}</span
            >
          </div>
          <p class="pm-candidate__description">
            {@html candidate.description}
          </p>
        </div>
      </div>
    {/each}
  </div></GraphicBlock
>

<style lang="scss">
  @use '@reuters-graphics/graphics-components/dist/scss/mixins' as mixins;

  h2 {
    margin: 14px 0;
    font-size: var(--theme-font-size-lg);
    line-height: 1.15;
  }
  .pm-candidates {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin: 0;
    padding: 0;
  }

  .pm-candidate {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
  }

  .pm-candidate__left {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 7rem;
  }

  .pm-candidate__right {
    flex: 1 1 auto;
    min-width: 0;
  }

  // squared headshot with a party-coloured mount peeking behind it — same
  // structure/proportions as graphics-components' Headpile `Headshot`
  .pm-candidate__headshot {
    width: 7rem;
    height: 6.75rem;
    position: relative;
    border-radius: 0.25rem;
    overflow: hidden;
    @include mixins.fmb-1;
  }

  .pm-candidate__mount {
    position: absolute;
    inset-block-end: 0;
    inset-inline-start: 0;
    width: 7rem;
    height: 4.75rem;
    border-radius: 0.25rem;
  }

  .pm-candidate__photo {
    width: 100%;
    height: 100%;
    background-size: cover;
    // top-anchored, unlike Headpile's own cropped/zoomed "bottom" framing —
    // keeps the whole head in frame instead of cutting it off
    background-position: center top;
    background-repeat: no-repeat;
    position: absolute;
    inset-block-end: 0;
    inset-inline-start: 0;
  }

  // Headpile's bold "name" slot, unswapped — one size down from its usual
  // text-base, per note below
  .pm-candidate__name {
    @include mixins.font-subhed;
    @include mixins.text-sm;
    @include mixins.font-semibold;
    @include mixins.leading-tight;
    margin: 0;
    text-align: center;
  }

  // sits where Headpile's "role" slot is — no divider line, unlike Headpile's `.role`
  .pm-candidate__party {
    margin: 0;
    @include mixins.fmb-1;

    span {
      display: inline-block;
      @include mixins.font-knowledge;
      @include mixins.text-xs;
      font-weight: 700;
      color: white;
      padding: 2px 6px;
      border-radius: 4px;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }
  }

  // one size down from its usual text-sm
  .pm-candidate__description {
    @include mixins.font-body;
    @include mixins.text-xs;
    @include mixins.leading-normal;
    margin: 0;
    @include mixins.font-newsreader-text;
  }
</style>
